/**
 * "Where did it go?" (ADR-0022). The bottom half, for the second or two after a scoring tap.
 *
 * Two ways to say where it went, and both draw the line from the striker to the touched point:
 *
 * - **Drag** from the centre (or from anywhere) to the point and lift. The line follows the finger
 *   and the zone name with it, so a thumb that landed on the wrong side is corrected before it
 *   lifts. Lifting records the ball: the lift is the commit.
 * - **Tap** the point directly. A tap has no slide to correct and no moment between touching and
 *   lifting, so recording it on the spot meant the line existed only while the finger was down and
 *   the sheet closed before it could be seen. So a tap *draws* the line and waits for **Save**; a
 *   second tap elsewhere moves it. A finger is not a point, so up to `TAP_SLOP_PX` of wander still
 *   counts as a tap.
 *
 * Four and six snap to the rope and beyond it, so only the direction has to be right.
 *
 * Two exits, in fixed places on every ball, and they do different things:
 *
 * - **Back** is "I tapped the wrong runs". Nothing is recorded and the pad returns. In edit mode
 *   it is Cancel.
 * - **Skip** records the ball with no direction. In edit mode it removes a direction already
 *   there. A scorer in a hurry, or who did not see where it went, is never held up by this.
 *
 * Nothing here builds a ledger event. It hands a `Shot` (or null) to its caller, which writes the
 * ball through the same `advance()` path as every other tap, so durability is not this file's
 * business.
 */
import { useRef, useState } from "react";
import { GROUND, shotFromOffset, sideOf, zoneOf } from "@sportsos/engine";
import type { BatterHand, Shot, WagonLine } from "@sportsos/engine";
import { WagonGround, type Cursor } from "./WagonWheel.tsx";

interface Props {
  readonly runs: number;
  readonly batterName: string;
  readonly hand: BatterHand;
  /** `NB`, `NB + W` and so on, so a latched no-ball stays visible above the ground. */
  readonly latchLabel: string;
  /** A ball already recorded is being corrected, not a new one scored. */
  readonly editing: boolean;
  /** The direction already on the ball, when correcting one that has it. */
  readonly initial: Shot | null;
  readonly onPlace: (shot: Shot) => void;
  readonly onSkip: () => void;
  readonly onBack: () => void;
  readonly onHand: (hand: BatterHand) => void;
}

/**
 * How far a finger may wander between touching and lifting and still be a tap, in CSS pixels.
 * Ten is the figure browsers themselves use for deciding between a tap and a scroll.
 */
export const TAP_SLOP_PX = 10;

export function WagonWheelSheet({
  runs,
  batterName,
  hand,
  latchLabel,
  editing,
  initial,
  onPlace,
  onSkip,
  onBack,
  onHand,
}: Props): React.ReactElement {
  const [cursor, setCursor] = useState<Shot | null>(initial);
  /** A tap has drawn this line and it is waiting for Save. Null while nothing is waiting. */
  const [held, setHeld] = useState<Shot | null>(null);
  /** A finger is on the ground. While it is, Save is hidden: the line under it is not what Save would record. */
  const [touching, setTouching] = useState(false);
  const surface = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  /** Where the current touch began, and whether it has wandered far enough to be a drag. */
  const origin = useRef<{ readonly x: number; readonly y: number } | null>(null);
  const wandered = useRef(false);

  /** The shot under a pointer, in the ground's own coordinates. */
  const shotAt = (clientX: number, clientY: number): Shot | null => {
    const box = surface.current?.getBoundingClientRect();
    if (box === undefined || box.width === 0) return null;
    const scale = GROUND.size / box.width;
    const dx = (clientX - box.left) * scale - GROUND.centre;
    const dy = (clientY - box.top) * scale - GROUND.centre;
    return shotFromOffset(dx, dy, GROUND.radius, runs);
  };

  const draw: Cursor | null =
    cursor === null ? null : { angle: cursor.wagonAngle, distance: cursor.wagonDistance, runs };
  const line: WagonLine[] = [];

  return (
    <div className="ds-sheet" style={{ padding: "var(--ds-space-3) var(--ds-space-3) var(--ds-space-4)" }}>
      <div className="row" style={{ gap: "var(--ds-space-2)", alignItems: "flex-start" }}>
        <span
          className="ds-tag"
          style={{
            flex: "none",
            background: runs >= 6 ? "var(--ds-cat-violet-fill)" : runs >= 4 ? "var(--ds-cat-teal-fill)" : "var(--ds-surface-hover)",
            color: runs >= 6 ? "var(--ds-cat-violet-ink)" : runs >= 4 ? "var(--ds-cat-teal-ink)" : "var(--ds-text)",
          }}
        >
          {editing ? "Edit · " : ""}
          {runs} {runs === 1 ? "run" : "runs"}
        </span>
        <span className="ds-caption" style={{ flex: 1, minWidth: 0, lineHeight: 1.3 }}>
          {editing ? "Where did this one go?" : runs >= 4 ? "Which way? It snaps to the rope" : "Touch where the ball went"}
          <br />
          <b style={{ color: "var(--ds-text)" }}>{batterName}</b>
          {latchLabel === "" ? "" : ` · ${latchLabel}`}
        </span>
        <div className="wagon-hand" role="group" aria-label="Which way the batter bats">
          <button type="button" aria-pressed={hand === "right"} onClick={() => onHand("right")}>RHB</button>
          <button type="button" aria-pressed={hand === "left"} onClick={() => onHand("left")}>LHB</button>
        </div>
      </div>

      <div
        ref={surface}
        className="wagon-sheet__ground"
        onPointerDown={(event) => {
          dragging.current = true;
          origin.current = { x: event.clientX, y: event.clientY };
          wandered.current = false;
          // The held line stays until this touch resolves: a tap replaces it, a drag records, and a
          // cancelled touch (below) brings it back.
          setTouching(true);
          // Keeps the slide tracking when a thumb drifts off the ground. Absent in some webviews,
          // and the gesture still works without it, so a refusal must not stop the touch.
          try {
            event.currentTarget.setPointerCapture(event.pointerId);
          } catch {
            // Nothing to do: the pointer is simply not captured.
          }
          setCursor(shotAt(event.clientX, event.clientY));
        }}
        onPointerMove={(event) => {
          if (!dragging.current) return;
          const start = origin.current;
          if (start !== null && Math.hypot(event.clientX - start.x, event.clientY - start.y) > TAP_SLOP_PX) {
            wandered.current = true;
          }
          setCursor(shotAt(event.clientX, event.clientY));
        }}
        onPointerUp={(event) => {
          if (!dragging.current) return;
          dragging.current = false;
          setTouching(false);
          const shot = shotAt(event.clientX, event.clientY);
          if (shot === null) return;
          if (wandered.current) {
            // A drag: the lift is the commit.
            onPlace(shot);
            return;
          }
          // A tap: draw the line and wait, so it can be seen before it is recorded.
          setCursor(shot);
          setHeld(shot);
        }}
        onPointerCancel={() => {
          // The browser took the gesture (a scroll, a system swipe). Nothing was chosen, and a line
          // an earlier tap was holding comes back rather than vanishing.
          dragging.current = false;
          setTouching(false);
          setCursor(held ?? initial);
        }}
      >
        <WagonGround lines={line} hand={hand} cursor={draw} title="Touch where the ball went" />
      </div>

      {/* Back and Skip flank the readout, in the same two places on every ball. They share its row
          rather than sitting under it because the bottom half is capped at 52% of the screen, and
          a separate row of 48px keys was what pushed them out of reach on a 667px phone. */}
      <div className="wagon-sheet__foot">
        <button type="button" className="ds-key" onClick={onBack}>
          {editing ? "Cancel" : "Back"}
        </button>
        {held === null || touching ? (
          <div className="wagon-sheet__read" aria-live="polite">
            {cursor === null ? (
              <small>Drag, or tap where it went</small>
            ) : (
              <>
                {zoneOf(cursor.wagonAngle, hand)}
                <small>{sideOf(cursor.wagonAngle, hand) === "off" ? "off side" : "leg side"}</small>
              </>
            )}
          </div>
        ) : (
          <button
            type="button"
            className="ds-key ds-key--primary"
            aria-label={`Save direction: ${zoneOf(held.wagonAngle, hand)}, ${sideOf(held.wagonAngle, hand)} side`}
            onClick={() => onPlace(held)}
          >
            Save · {zoneOf(held.wagonAngle, hand)}
          </button>
        )}
        <button type="button" className="ds-key" onClick={onSkip}>
          {editing ? "Remove direction" : "Skip"}
        </button>
      </div>
    </div>
  );
}
